import { useRef, useState, type ChangeEvent, type DragEvent } from "react";
import * as XLSX from "xlsx";
import { coverageDestinations } from "@/mocks/coverage";

type RowType = "Mobile" | "Fixed";

interface ComparisonRow {
  country: string;
  prefix: string;
  type: RowType;
  yourRate: number;
  ourRate: number | null;
  matched: boolean;
  savingsPct: number | null;
}

type Status = "idle" | "parsing" | "error";

const MAX_SIZE = 5 * 1024 * 1024; // 5 MB

function normalizeType(raw: string): RowType {
  const t = raw.trim().toLowerCase();
  if (t.includes("fixed") || t.includes("landline") || t.includes("geographic")) return "Fixed";
  return "Mobile";
}

function findOur(country: string, prefix: string) {
  const c = country.trim().toLowerCase();
  const p = prefix.trim();
  return (
    coverageDestinations.find((d) => d.country.toLowerCase() === c) ||
    coverageDestinations.find((d) => d.prefix === p) ||
    null
  );
}

function parseRateDeck(file: File): Promise<ComparisonRow[]> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("Could not read the file. Please try again."));
    reader.onload = (e) => {
      try {
        const isCsv = /\.csv$/i.test(file.name);
        const wb = isCsv
          ? XLSX.read(e.target?.result as string, { type: "string" })
          : XLSX.read(e.target?.result as ArrayBuffer, { type: "array" });

        const ws = wb.Sheets[wb.SheetNames[0]];
        if (!ws) throw new Error("The file appears to be empty.");

        const raw = XLSX.utils.sheet_to_json<unknown[]>(ws, { header: 1, blankrows: false }) as unknown[][];
        if (!raw.length) throw new Error("No data found in the file.");

        const header = (raw[0] || []).map((h) => String(h).trim().toLowerCase());
        const idx = {
          country: header.indexOf("country"),
          prefix: header.indexOf("prefix"),
          type: header.indexOf("type"),
          rate: header.indexOf("rate"),
        };

        if (idx.country === -1 || idx.prefix === -1 || idx.rate === -1) {
          throw new Error('Missing required columns. Expected: Country, Prefix, Type, Rate.');
        }

        const rows: ComparisonRow[] = [];
        for (let i = 1; i < raw.length; i += 1) {
          const row = raw[i] || [];
          const country = String(row[idx.country] ?? "").trim();
          const prefix = String(row[idx.prefix] ?? "").trim();
          const rateNum = Number(String(row[idx.rate] ?? "").replace(/[$,]/g, "").trim());

          if (!country || !prefix || Number.isNaN(rateNum) || rateNum < 0) continue;

          const type: RowType = idx.type === -1 ? "Mobile" : normalizeType(String(row[idx.type] ?? ""));
          const our = findOur(country, prefix);

          let ourRate: number | null = null;
          if (our) ourRate = type === "Fixed" ? our.fixedRate : our.mobileRate;

          const savingsPct =
            ourRate !== null && rateNum > 0 ? ((rateNum - ourRate) / rateNum) * 100 : null;

          rows.push({
            country,
            prefix,
            type,
            yourRate: rateNum,
            ourRate,
            matched: ourRate !== null,
            savingsPct,
          });
        }

        if (!rows.length) throw new Error("No valid rate rows found. Check your columns and try again.");
        resolve(rows);
      } catch (err) {
        reject(err instanceof Error ? err : new Error("Could not parse this file."));
      }
    };

    if (/\.csv$/i.test(file.name)) reader.readAsText(file);
    else reader.readAsArrayBuffer(file);
  });
}

function formatRate(v: number | null): string {
  return v === null ? "—" : `$${v.toFixed(4)}`;
}

export default function RateDeckImport() {
  const inputRef = useRef<HTMLInputElement>(null);
  const [status, setStatus] = useState<Status>("idle");
  const [error, setError] = useState("");
  const [fileName, setFileName] = useState("");
  const [rows, setRows] = useState<ComparisonRow[]>([]);
  const [dragOver, setDragOver] = useState(false);
  const [toast, setToast] = useState("");
  const toastTimer = useRef<number | null>(null);

  const showToast = (msg: string) => {
    setToast(msg);
    if (toastTimer.current) window.clearTimeout(toastTimer.current);
    toastTimer.current = window.setTimeout(() => setToast(""), 4500);
  };

  const handleFile = async (file: File) => {
    setStatus("parsing");
    setError("");
    setRows([]);
    setFileName("");

    if (!/\.(csv|xlsx|xls)$/i.test(file.name)) {
      setStatus("error");
      setError("Unsupported file type. Please upload a .csv, .xlsx, or .xls file.");
      return;
    }
    if (file.size > MAX_SIZE) {
      setStatus("error");
      setError("File is too large. Please upload a file under 5 MB.");
      return;
    }

    try {
      const parsed = await parseRateDeck(file);
      setRows(parsed);
      setFileName(file.name);
      setStatus("idle");
      const matchedCount = parsed.filter((r) => r.matched).length;
      showToast(`Rate deck imported — ${parsed.length} rows, ${matchedCount} matched to our coverage.`);
    } catch (err) {
      setStatus("error");
      setError(err instanceof Error ? err.message : "Could not parse this file.");
    }
  };

  const onInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) void handleFile(file);
    e.target.value = "";
  };

  const onDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) void handleFile(file);
  };

  const downloadSample = () => {
    const header = "Country,Prefix,Type,Rate";
    const sample = [
      "United States,+1,Mobile,0.0090",
      "United Kingdom,+44,Mobile,0.0078",
      "Germany,+49,Fixed,0.0021",
      "Brazil,+55,Mobile,0.0125",
      "Nigeria,+234,Mobile,0.0072",
    ];
    const csv = [header, ...sample].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "voxmaster-rate-deck-template.csv";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const matched = rows.filter((r) => r.matched).length;
  const weBeat = rows.filter((r) => r.matched && (r.savingsPct ?? 0) > 0.0001).length;
  const weHigher = rows.filter((r) => r.matched && (r.savingsPct ?? 0) < -0.0001).length;
  const equal = rows.filter((r) => r.matched && Math.abs(r.savingsPct ?? 0) <= 0.0001).length;
  const noMatch = rows.length - matched;

  return (
    <section className="py-14 md:py-20">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="rounded-2xl border border-background-200 bg-background-50 p-8 md:p-10">
          <div className="flex items-center gap-3 mb-6">
            <span className="w-11 h-11 rounded-lg bg-accent-100 text-accent-700 flex items-center justify-center">
              <i className="ri-upload-cloud-line text-xl w-6 h-6 flex items-center justify-center" />
            </span>
            <div>
              <h3 className="text-lg font-semibold text-foreground-950 font-heading">Import your existing rate deck</h3>
              <p className="text-xs text-foreground-600 mt-0.5">CSV / XLSX compatible</p>
            </div>
          </div>

          <p className="text-sm text-foreground-700 leading-relaxed max-w-3xl">
            Already have a rate deck from your current carrier? Upload it and we&apos;ll match our rates line-by-line against your destinations. You&apos;ll get a comparison report showing where we beat your current pricing — and where we recommend a different route tier for better quality. No manual re-entry, no spreadsheet gymnastics.
          </p>

          <input
            ref={inputRef}
            type="file"
            accept=".csv,.xlsx,.xls"
            className="hidden"
            onChange={onInputChange}
          />

          <div
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={onDrop}
            className={`mt-6 rounded-xl border-2 border-dashed p-8 text-center transition-colors ${
              dragOver ? "border-primary-400 bg-primary-100/50" : "border-background-300 bg-background-100/50"
            }`}
          >
            <i className="ri-file-upload-line text-3xl text-foreground-400 w-8 h-8 flex items-center justify-center mx-auto" />
            <p className="mt-3 text-sm text-foreground-700">
              Drop your file here, or{" "}
              <button
                type="button"
                onClick={() => inputRef.current?.click()}
                className="text-primary-600 font-semibold hover:text-primary-700 underline underline-offset-2 cursor-pointer"
              >
                browse
              </button>
            </p>
            <p className="mt-1 text-xs text-foreground-500">Supported: CSV, XLSX with columns: Country, Prefix, Type, Rate · Max 5 MB</p>
            <button
              type="button"
              onClick={downloadSample}
              className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-accent-700 hover:text-accent-800 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-download-2-line w-4 h-4 flex items-center justify-center" />
              Download sample CSV template
            </button>
          </div>

          {status === "parsing" && (
            <div className="mt-5 flex items-center gap-2 text-sm text-foreground-600">
              <i className="ri-loader-4-line animate-spin w-4 h-4 flex items-center justify-center" />
              Analyzing your rate deck…
            </div>
          )}

          {status === "error" && (
            <div className="mt-5 rounded-md border border-primary-300 bg-primary-100 text-primary-900 text-sm px-4 py-3 flex items-start gap-2">
              <i className="ri-error-warning-line w-5 h-5 flex items-center justify-center mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {rows.length > 0 && (
            <div className="mt-8">
              <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
                <div className="flex items-center gap-2">
                  <i className="ri-file-check-line text-accent-600 w-5 h-5 flex items-center justify-center" />
                  <span className="text-sm font-medium text-foreground-950">{fileName}</span>
                  <span className="text-xs text-foreground-500">· {rows.length} rows</span>
                </div>
                <button
                  type="button"
                  onClick={() => { inputRef.current?.click(); setRows([]); setFileName(""); }}
                  className="text-sm text-foreground-600 hover:text-primary-600 cursor-pointer whitespace-nowrap"
                >
                  Upload a different file
                </button>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-6">
                {[
                  { label: "Rows imported", value: rows.length, tone: "text-foreground-950" },
                  { label: "Matched", value: matched, tone: "text-accent-700" },
                  { label: "We beat", value: weBeat, tone: "text-accent-700" },
                  { label: "We're higher", value: weHigher, tone: "text-secondary-700" },
                  { label: "No match", value: noMatch, tone: "text-foreground-500" },
                ].map((s) => (
                  <div key={s.label} className="rounded-lg border border-background-200 bg-background-50 px-4 py-3">
                    <p className={`text-2xl font-semibold font-heading ${s.tone}`}>{s.value}</p>
                    <p className="text-xs text-foreground-500 mt-0.5">{s.label}</p>
                  </div>
                ))}
              </div>

              {weBeat > 0 && (
                <div className="mb-6 rounded-lg border border-accent-300 bg-accent-100 px-4 py-3 flex items-start gap-2 text-sm text-accent-900">
                  <i className="ri-trophy-line w-5 h-5 flex items-center justify-center mt-0.5 shrink-0" />
                  <span>
                    We beat your current pricing on <strong>{weBeat}</strong> of {matched} matched destinations — up to{" "}
                    <strong>{Math.max(...rows.map((r) => r.savingsPct ?? 0)).toFixed(1)}%</strong> cheaper per minute.
                  </span>
                </div>
              )}

              <div className="overflow-x-auto rounded-xl border border-background-200 bg-background-50">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-background-200 text-left text-xs uppercase tracking-wider text-foreground-500 bg-background-100/60">
                      <th className="px-4 py-3 font-medium">Country</th>
                      <th className="px-4 py-3 font-medium">Prefix</th>
                      <th className="px-4 py-3 font-medium">Type</th>
                      <th className="px-4 py-3 font-medium">Your rate</th>
                      <th className="px-4 py-3 font-medium">Our rate</th>
                      <th className="px-4 py-3 font-medium">Result</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r, i) => {
                      let badge = "bg-background-100 text-foreground-500";
                      let label = "No match";
                      if (r.matched && (r.savingsPct ?? 0) > 0.0001) {
                        badge = "bg-accent-100 text-accent-800";
                        label = `We beat · ${(r.savingsPct ?? 0).toFixed(1)}%`;
                      } else if (r.matched && (r.savingsPct ?? 0) < -0.0001) {
                        badge = "bg-secondary-100 text-secondary-800";
                        label = `We're higher · ${Math.abs(r.savingsPct ?? 0).toFixed(1)}%`;
                      } else if (r.matched) {
                        badge = "bg-background-100 text-foreground-700";
                        label = "Match";
                      }
                      return (
                        <tr key={`${r.country}-${i}`} className="border-b border-background-200 last:border-0 hover:bg-background-100/40 transition-colors">
                          <td className="px-4 py-3 font-medium text-foreground-950">{r.country}</td>
                          <td className="px-4 py-3 text-foreground-600 font-mono text-xs">{r.prefix}</td>
                          <td className="px-4 py-3 text-foreground-700">{r.type}</td>
                          <td className="px-4 py-3 text-foreground-700 font-mono text-xs">{formatRate(r.yourRate)}</td>
                          <td className="px-4 py-3 text-foreground-700 font-mono text-xs">{formatRate(r.ourRate)}</td>
                          <td className="px-4 py-3">
                            <span className={`text-[11px] px-2 py-1 rounded-full font-medium ${badge}`}>{label}</span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <p className="mt-4 text-xs text-foreground-500">
                Comparison is illustrative against our current published rate deck. A carrier-relations rep will confirm final pricing during onboarding.
              </p>
            </div>
          )}
        </div>
      </div>

      {toast && (
        <div
          role="status"
          aria-live="polite"
          className="fixed bottom-6 right-6 z-50 max-w-sm rounded-lg border border-accent-300 bg-accent-100 text-accent-900 px-4 py-3 shadow-sm flex items-start gap-3 animate-[page-enter_0.3s_ease]"
        >
          <i className="ri-checkbox-circle-line text-accent-700 w-5 h-5 flex items-center justify-center mt-0.5 shrink-0" />
          <span className="text-sm leading-snug">{toast}</span>
          <button
            type="button"
            onClick={() => setToast("")}
            aria-label="Dismiss"
            className="ml-1 text-accent-700 hover:text-accent-900 w-5 h-5 flex items-center justify-center cursor-pointer shrink-0"
          >
            <i className="ri-close-line w-4 h-4 flex items-center justify-center" />
          </button>
        </div>
      )}
    </section>
  );
}